import { TextField } from "@simplybusiness/mobius";
import type { ChangeEvent } from "react";
import { useEffect, useRef } from "react";
import { useQuestionnaire } from "../../../../../../../features/questionnaire";
import { useAnswer } from "../../../../../../../features/questionnaire";
import type { QuestionComponentProps } from "../../types";
import { useZipCodeValidation } from "./useZipCodeValidation";
import { useUpdateCity } from "./useUpdateCity";
import { US_STATE_ANSWER_KEY } from "../../../../../../../constants/constants";

export const ZipCodeQuestion = ({
  questionFieldProps,
}: QuestionComponentProps) => {
  const {
    value,
    onChange,
    readOnly,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const { id, name } = questionFieldProps;
  const { setAsyncError, clearAsyncError } = useQuestionnaire();
  const stateValue = useAnswer(US_STATE_ANSWER_KEY) as string | undefined;

  const zipCodeValue = value as string | undefined;

  const { isValid, validationMessage, city } = useZipCodeValidation({
    value: zipCodeValue,
    state: stateValue,
  });

  // Automatically update city answer when it changes from zip code validation
  useUpdateCity({ city, isValid });

  // Track previous validation state to avoid unnecessary updates
  const previousIsValidRef = useRef<boolean>(isValid);
  const previousValidationMessageRef = useRef<string | undefined>(
    validationMessage,
  );

  useEffect(() => {
    const hasValidationChanged =
      previousIsValidRef.current !== isValid ||
      previousValidationMessageRef.current !== validationMessage;

    if (hasValidationChanged && name) {
      if (!isValid && validationMessage) {
        setAsyncError(name, validationMessage);
      } else {
        clearAsyncError(name);
      }

      // Update refs to current values
      previousIsValidRef.current = isValid;
      previousValidationMessageRef.current = validationMessage;
    }
  }, [isValid, validationMessage, name, setAsyncError, clearAsyncError]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // Only allow numeric input and limit to 5 characters
    const numericValue = value.replace(/\D/g, "").slice(0, 5);

    onChange?.({
      target: { name, value: { [id!]: numericValue, [name]: numericValue } },
    });
  };

  const handleBlur = (event: React.FocusEvent) => {
    questionFieldProps.onBlur?.(event);
  };

  return (
    <TextField
      {...otherProps}
      type="text"
      inputMode="numeric"
      pattern="[0-9]{5}"
      placeholder="12345"
      maxLength={5}
      value={zipCodeValue}
      onChange={handleChange}
      onBlur={handleBlur}
      isReadOnly={!!readOnly}
    />
  );
};
